import React from "react";
import { icons } from "../../constants";
import { stringDateToNumber } from "../../utils/functions/stringDateToNumber";
import { useCountDown } from "../../utils/hooks";
import "./style.scss";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { MY_TZ } from "../../utils/constants";
import retireBackground from "../../assets/images/retire-campaign-bg.png";
import cnyBackground from "../../assets/images/bg-cny-campaign.png"

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * @typedef {object} Campaign
 * @property {string} startDate
 * @property {string} endDate
 * @property {string} title
 * @property {React.ReactNode} children
 * @property {() => void} onCashIn
 * @property {() => void} openPDF
 * @property {"rsp" | "lebaran" | "christmas" | "lunar" | "announcement"} type
 */

/**
 * Campaign component.
 *
 * @type {React.FC<Campaign>}
 * @returns {React.ReactElement}
 */
const CampaignCard = ({
  startDate,
  endDate,
  title = "",
  children,
  onCashIn,
  openPDF,
  type,
  className= "",
}) => {
  const now = new Date().getTime();
  const startDateInNum = stringDateToNumber(startDate);
  const timeCount = useCountDown(startDateInNum);

  if (type === "prs") {
    return (
      <div className="c-card c-card--prs">
        <img src={retireBackground} alt="Retirement" />
        <div className="c-card__topbar c-card__topbar--prs">
          <img src={icons.bell} alt="Announcement icon" />
        </div>
        {children}
      </div>
    );
  }

  if (type === "announcement") {
    return (
      <div className="c-card c-card--announcement">
        <div className="c-card__topbar c-card__topbar--announcement">
          <img src={icons.announcementSvg} alt="Announcement icon" />
          <p>{title}</p>
        </div>
        {children}
      </div>
    );
  }

  if (type === "lebaran") {
    return (
      <div className={`c-card ${type && `c-card--${type}`}`}>
        {/* <img src={cnyBackground} alt="CNY Campaign Promo" /> */}
        <div className="c-card__container">
          <div className="c-card__topbar">
            <img src={icons.gift} alt="Campaign reward" />
            <p onClick={openPDF} className="c-card__link">
              Learn More
            </p>
          </div>
          {children}
          {now >= startDateInNum ? (
            <div className="c-card__end-section">
              {/* <p className="c-card__end-title">
                *Gross returns excluding fees{" "}
              </p> */}
              <button onClick={onCashIn} className="c-card__cash-in">
                Cash In
              </button>
            </div>
          ) : (
            <p className="c-card__countdown">
              Starting in: <span>{timeCount.days}</span> Day{" "}
              <span>{timeCount.hours}</span> hour{" "}
              <span>{timeCount.minutes}</span> min{" "}
              <span>{timeCount.seconds}</span> sec
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`c-card ${type && `c-card--${type}`} ${className}`}>
      <div className="c-card__topbar">
        <div className="c-card__wrapper">
          <img src={icons.gift} alt="Campaign reward" />
          <p className="c-card__period-time">
            {dayjs.utc(startDate).tz(MY_TZ).format("DD MMM YY")} -{" "}
            {dayjs.utc(endDate).tz(MY_TZ).format("DD MMM YY")}
          </p>
        </div>
        <p onClick={openPDF} className="c-card__link">
          Learn More
        </p>
      </div>
      {children}
      {now >= startDateInNum ? (
        <div className="c-card__end-section">
          <p className="c-card__end-title">T&Cs Apply.</p>
          <button onClick={onCashIn} className="c-card__cash-in">
            Cash In
          </button>
        </div>
      ) : (
        <p className="c-card__countdown">
          Starting in: <span>{timeCount.days}</span> Day{" "}
          <span>{timeCount.hours}</span> hour <span>{timeCount.minutes}</span>{" "}
          min <span>{timeCount.seconds}</span> sec
        </p>
      )}
    </div>
  );
};

CampaignCard.propTypes = {
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  onCashIn: PropTypes.func,
  openPDF: PropTypes.func,
};

export default CampaignCard;
